import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Mail, ArrowLeft, CheckCircle2, AlertCircle, Sparkles } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';

const BRAND_NAME = 'Himil Fleet';
const BRAND_LOGO_SRC = '/Himil-logo.png';

const InviteActivation = () => {
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [localError, setLocalError] = useState('');

  const { resetPassword } = useAuth();

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!email.trim()) {
      setLocalError('Please enter the email address your admin invited.');
      return;
    }

    if (!email.includes('@')) {
      setLocalError('Enter a valid email address.');
      return;
    }

    try {
      setIsLoading(true);
      setLocalError('');
      const response = await resetPassword(email.trim());

      if (response.success) {
        setSuccess(true);
      } else {
        setLocalError(response.error || 'We could not send the activation email.');
      }
    } catch (error) {
      setLocalError(error?.message || 'Something went wrong. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-6 text-center">
          <div className="mx-auto h-14 w-14 flex items-center justify-center rounded-full bg-green-600">
            <CheckCircle2 className="h-8 w-8 text-white" />
          </div>
          <h2 className="text-3xl font-bold text-gray-900">Check your inbox</h2>
          <p className="text-sm text-gray-600">
            We sent an activation email to <strong>{email}</strong>. Use the link inside to set your password and finish creating your {BRAND_NAME} account.
          </p>
          <p className="text-xs text-gray-500">
            If you do not see the message within a few minutes, check your spam folder or ask your administrator to confirm your email address.
          </p>
          <div>
            <Link to="/login" className="inline-flex items-center text-sm font-medium text-brand-mid hover:text-brand-dark">
              <ArrowLeft className="h-4 w-4 mr-1" />
              Back to sign in
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div className="text-center space-y-3">
          <div className="mx-auto h-16 w-16 flex items-center justify-center">
            <img src={BRAND_LOGO_SRC} alt={`${BRAND_NAME} logo`} className="h-full w-auto" />
          </div>
          <h2 className="text-3xl font-extrabold text-gray-900">Activate your invite</h2>
          <p className="text-sm text-gray-600">
            Enter the email address your administrator invited. We will email you a secure link to create your account and set a password.
          </p>
          <div className="flex items-center justify-center text-xs text-gray-500 space-x-2">
            <Sparkles className="h-4 w-4 text-brand-mid" />
            <span>No passwords are stored here—your link comes directly from Firebase Auth.</span>
          </div>
        </div>

        <form className="space-y-6" onSubmit={handleSubmit}>
          {localError && (
            <div className="rounded-md bg-red-50 p-4 text-sm text-red-700 flex">
              <AlertCircle className="h-5 w-5 text-red-400 mr-2" />
              <span>{localError}</span>
            </div>
          )}

          <div>
            <label htmlFor="email" className="block text-sm font-medium text-gray-700">
              Work email
            </label>
            <div className="mt-1 relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Mail className="h-5 w-5 text-gray-400" />
              </div>
              <input
                id="email"
                name="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="appearance-none block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md placeholder-gray-400 focus:outline-none focus:ring-brand-mid focus:border-brand-mid text-sm"
                placeholder="you@company.com"
              />
            </div>
          </div>

          <button
            type="submit"
            disabled={isLoading}
            className="w-full flex justify-center py-2 px-4 btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isLoading ? 'Sending link...' : 'Send activation email'}
          </button>

          <div className="text-center text-sm text-gray-600">
            Already activated your invite?{' '}
            <Link to="/login" className="font-medium text-brand-mid hover:text-brand-dark">
              Go back to sign in
            </Link>
          </div>
        </form>
      </div>
    </div>
  );
};

export default InviteActivation;
